import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { LogtoProvider } from '@logto/react';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { retrieveLaunchParams } from '@tma.js/sdk-react';

import { store } from '@/store';
import { router } from '@/routes/router';
import { i18n } from '@/lib/i18n';
import { initTelegram } from '@/lib/telegram';
import { applyPalette, loadStoredAppearance, resolvePalette } from '@/lib/appearance';
import { logtoConfig } from '@/lib/logto';
import { EnvUnsupported } from '@/components/common/EnvUnsupported';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { AuthCallback } from '@/routes/AuthCallback';
import { env } from '@/env';

import './styles/globals.css';
import { setupMockEnv } from './mockEnv';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');
const root = ReactDOM.createRoot(rootEl);

// The auth-bridge round trip lands here from the system browser after Logto
// auth (PROD flow): tma.qlash.gg/auth/callback?code=...&state=... in Safari,
// with NO Telegram WebApp context. Without this short-circuit the normal
// bootstrap would call retrieveLaunchParams(), throw, render <EnvUnsupported>
// — and the BridgeCallback that deep-links the code back into the bot would
// never run. Render AuthCallback directly so BridgeCallback can do its job
// (build the t.me?startapp=auth_<code> link and navigate to it).
function isBridgeCallback(): boolean {
  if (window.location.pathname !== '/auth/callback') return false;
  const initData = (window as unknown as {
    Telegram?: { WebApp?: { initData?: string } };
  }).Telegram?.WebApp?.initData;
  return !initData;
}

async function bootstrap() {
  if (isBridgeCallback()) {
    // BridgeCallback only reads window.location.search + writes window.location.href,
    // no router/Logto/Redux providers required. Wrapped in ErrorBoundary so a
    // crash here still surfaces something instead of a white screen.
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <AuthCallback />
        </ErrorBoundary>
      </StrictMode>,
    );
    return;
  }

  try {
    await setupMockEnv();

    const launchParams = retrieveLaunchParams();
    const platform = launchParams.tgWebAppPlatform;
    const debug = (launchParams.tgWebAppStartParam || '').includes('debug') || env.isDev;

    await initTelegram({
      debug,
      eruda: debug && env.enableEruda && ['ios', 'android'].includes(platform),
      mockForMacOS: platform === 'macos',
    });

    // Apply the persisted theme before the first React paint (no FOUC). The
    // slice hydrates the same prefs into Redux; useAppearance() keeps it in
    // sync from here on.
    applyPalette(resolvePalette(loadStoredAppearance()));

    root.render(
      <StrictMode>
        <ErrorBoundary>
          <LogtoProvider config={logtoConfig}>
            <ReduxProvider store={store}>
              <I18nextProvider i18n={i18n}>
                <RouterProvider router={router} />
              </I18nextProvider>
            </ReduxProvider>
          </LogtoProvider>
        </ErrorBoundary>
      </StrictMode>,
    );
  } catch (err) {

    console.error('[bootstrap] failed to initialize:', err);
    root.render(<EnvUnsupported />);
  }
}

void bootstrap();
